import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO_URL, siteConfig } from "../../data/mockData";
import { useAuth } from "../../context/AuthContext";
import { useSiteData } from "../../context/SiteDataContext";
import { LoginModal, RegisterModal } from "../auth/AuthModals";
import { Menu, X, Send, User, LogIn } from "lucide-react";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const { user } = useAuth();
  const { socialLinks: sl, siteConfig: sc } = useSiteData();
  const navigate = useNavigate();
  const socialLinks = sl || {};
  const logoUrl = sc?.logoUrl || LOGO_URL;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: "#about" },
    { label: "Features", href: "#features" },
    { label: "How to Buy", href: "#howtobuy" },
    { label: "Tokenomics", href: "#tokenomics" },
    { label: "Roadmap", href: "#roadmap" },
    { label: "FAQ", href: "#faq" },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-[#060d06]/95 backdrop-blur-md shadow-lg shadow-black/30"
            : "bg-[#060d06]"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="#" className="flex items-center gap-3 group">
              <img
                src={logoUrl}
                alt="Marsupilami Coin"
                className="w-10 h-10 rounded-full border-2 border-yellow-400 group-hover:scale-110 transition-transform duration-300"
              />
              <span className="text-xl font-bold">
                <span className="text-yellow-400">MARSUPI</span>
                <span className="text-white">LAMI</span>
              </span>
            </a>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="text-gray-300 hover:text-yellow-400 transition-colors duration-200 text-sm font-medium"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              {/* Auth Buttons */}
              {user ? (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="hidden sm:flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 rounded-lg px-4 py-2 text-yellow-400 hover:bg-yellow-400/20 transition-all text-sm font-medium"
                >
                  <User size={16} />
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={() => setShowLogin(true)}
                  className="hidden sm:flex items-center gap-2 text-gray-300 hover:text-yellow-400 transition-colors text-sm font-medium"
                >
                  <LogIn size={16} />
                  LOGIN / REGISTER
                </button>
              )}

              <a
                href={socialLinks.telegram || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
              >
                <Send size={16} />
              </a>
              <a
                href={socialLinks.twitter || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24h-2.19L17.61 20.644Z" />
                </svg>
              </a>
              {/* Mobile Menu */}
              <button
                className="lg:hidden text-yellow-400 ml-2"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
        {/* Yellow accent line */}
        <div className="h-[2px] bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      </nav>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-[#060d06] backdrop-blur-xl pt-24 px-6 lg:hidden overflow-y-auto animate-in fade-in duration-300">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="text-gray-300 hover:text-yellow-400 text-lg font-medium text-left py-2 border-b border-gray-800"
              >
                {link.label}
              </button>
            ))}
            {user ? (
              <button
                onClick={() => { setMobileOpen(false); navigate("/dashboard"); }}
                className="text-yellow-400 text-lg font-medium text-left py-2 border-b border-gray-800"
              >
                Dashboard
              </button>
            ) : (
              <button
                onClick={() => { setMobileOpen(false); setShowLogin(true); }}
                className="text-yellow-400 text-lg font-medium text-left py-2 border-b border-gray-800"
              >
                LOGIN / REGISTER
              </button>
            )}
          </div>
        </div>
      )}

      {/* Auth Modals */}
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToRegister={() => { setShowLogin(false); setShowRegister(true); }}
      />
      <RegisterModal
        isOpen={showRegister}
        onClose={() => setShowRegister(false)}
        onSwitchToLogin={() => { setShowRegister(false); setShowLogin(true); }}
      />
    </>
  );
};

export default Navbar;
